import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BadgeCheck, CreditCard, Minus, Plus, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, money, money2, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RentalAgreement } from "@/components/RentalAgreement";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";

const dayDiff = (start, end) =>
  Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86400000) + 1;

const inputCls =
  "h-12 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal focus-visible:ring-2 focus-visible:ring-royal/30";

export const BookingWidget = ({ listing, booked, inventory = null, packageQty = 0 }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rebookId = searchParams.get("rebook");
  const [rebook, setRebook] = useState(null);

  useEffect(() => {
    if (rebookId && user) {
      api.get("/bookings/my").then((r) => {
        const found = r.data.find((b) => b.id === rebookId);
        if (found) setRebook(found);
      }).catch(() => {});
    }
  }, [rebookId, user]);
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [extraDollies, setExtraDollies] = useState(0);
  const [selfPickup, setSelfPickup] = useState(false);
  const [deliveryWindow, setDeliveryWindow] = useState("morning");
  const [pickupWindow, setPickupWindow] = useState("morning");
  const [open, setOpen] = useState(false);
  const [pause, setPause] = useState(null);

  useEffect(() => {
    api.get("/rental-status").then((r) => setPause(r.data)).catch(() => {});
  }, []);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "", zip: "", card_holder: "", license_number: "", address: "",
    purpose: "residential", alt_name: "", alt_phone: "", alt_email: "", notes: "", referred_by: "",
  });

  const isWeekly = listing.billing === "weekly";
  const isOnce = listing.billing === "once";
  const isDual = listing.billing === "dual";
  const isTiered = listing.billing === "tiered";
  const weekPrices = listing.week_prices || [];
  const [withPackage, setWithPackage] = useState(false);
  const [packingKit, setPackingKit] = useState(false);
  const [dollyBundle, setDollyBundle] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [durationWeeks, setDurationWeeks] = useState(() => {
    const w = parseInt(searchParams.get("weeks") || "", 10);
    return Number.isFinite(w) ? Math.min(Math.max(w, 1), Math.max(weekPrices.length, 1)) : 1;
  });
  const weeklyLike = isWeekly || (isDual && withPackage);
  const isPackage = listing.category === "Tote Packages";
  const days = start && end && end >= start ? dayDiff(start, end) : 0;
  const extraDays = weeklyLike ? Math.max(0, days - 7) : 0;
  const weeks = Math.max(1, Math.ceil(days / 7));
  const rental = isOnce
    ? days
      ? listing.flat_rate || 0
      : 0
    : isTiered
      ? days
        ? weekPrices[weeks - 1] || 0
        : 0
      : weeklyLike
        ? days > 0
          ? listing.weekly_rate + extraDays * (listing.extra_day_rate || 0)
          : 0
        : days * listing.daily_rate;
  const tierSavings = isTiered && days ? Math.max(0, (weekPrices[0] || 0) * weeks - rental) : 0;
  const dollyFee = days ? extraDollies * 25 * weeks : 0;
  const discount = selfPickup && !isOnce ? 25 : 0;
  const kitFee = isTiered && packingKit ? 20 : 0;
  const bundleFee = isTiered && dollyBundle ? 25 * weeks : 0;
  const subtotal = Math.max(0, rental + dollyFee + bundleFee - discount);
  const returningDisc = user?.returning_discount ? Math.round(subtotal * 10) / 100 : 0;
  const total = subtotal - returningDisc + kitFee;
  const reserveNow = isOnce ? total : Math.round((subtotal - returningDisc) * 50) / 100 + kitFee;
  const balanceDue = total - reserveNow;

  const overlap = useMemo(() => {
    if (!start || !end || days <= 0) return false;
    return booked.some((r) => start <= r.end_date && end >= r.start_date);
  }, [start, end, days, booked]);

  const totesLeft = useMemo(() => {
    if (!packageQty || inventory == null || !start || !end || days <= 0) return null;
    const used = booked
      .filter((r) => start <= r.end_date && end >= r.start_date)
      .reduce((s, r) => s + (r.tote_qty || 0), 0);
    return Math.max(0, inventory - used);
  }, [start, end, days, booked, packageQty, inventory]);

  const insufficient = packageQty ? (totesLeft != null && totesLeft < packageQty) : false;
  const blocked = packageQty ? insufficient : overlap;

  const pickStart = (v) => {
    setStart(v);
    if (weeklyLike && v) {
      const d = new Date(`${v}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + 6);
      setEnd(d.toISOString().slice(0, 10));
    }
    if (isTiered && v) {
      const d = new Date(`${v}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + 7 * durationWeeks - 1);
      setEnd(d.toISOString().slice(0, 10));
    }
    if (isOnce && v) {
      setEnd(v);
    }
  };

  const reserve = () => {
    if (pause?.paused) return toast.error(pause.message);
    if (days <= 0) return toast.error("Pick a valid date range first");
    if (blocked) return toast.error(packageQty
      ? "Not enough totes left for those dates — try different dates or a smaller package"
      : "Those dates overlap an existing reservation");
    if (!user) {
      toast.error("Sign in to reserve this gear");
      return navigate("/auth", { state: { from: location.pathname } });
    }
    setCustomer({
      name: rebook?.customer?.name || user.name || "",
      email: rebook?.customer?.email || user.email || "",
      phone: rebook?.customer?.phone || "",
      zip: rebook?.customer?.zip || sessionStorage.getItem("otr_zip") || "",
      card_holder: rebook?.customer?.card_holder || "",
      license_number: rebook?.customer?.license_number || "",
      address: rebook?.customer?.address || "",
      purpose: rebook?.customer?.purpose || "residential",
      alt_name: rebook?.customer?.alt_name || "",
      alt_phone: rebook?.customer?.alt_phone || "",
      alt_email: rebook?.customer?.alt_email || "",
      notes: rebook?.customer?.notes || "",
      referred_by: rebook?.customer?.referred_by || "",
    });
    if (rebook) {
      setExtraDollies(Math.max(0, (rebook.dollies || 1) - 1));
      setSelfPickup(!!rebook.self_pickup);
      setPackingKit(!!rebook.packing_kit);
      setDollyBundle(!!rebook.dolly_bundle);
      if (rebook.delivery_window) setDeliveryWindow(rebook.delivery_window);
      if (rebook.pickup_window) setPickupWindow(rebook.pickup_window);
      toast.success("Details from your last rental are pre-filled — just pick your dates.");
    }
    setError("");
    setAgreed(false);
    setStep(0);
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/payments/rental-checkout", {
        listing_id: listing.id,
        start_date: start,
        end_date: end,
        customer,
        extra_dollies: extraDollies,
        self_pickup: selfPickup,
        with_package: isDual && withPackage,
        packing_kit: isTiered && packingKit,
        dolly_bundle: isTiered && dollyBundle,
        agreed,
        delivery_window: deliveryWindow,
        pickup_window: pickupWindow,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setError(msg);
      if (e.response?.status === 409) setStep(0);
      if (e.response?.status === 403) setStep(1);
      toast.error(msg);
      setBusy(false);
    }
  };

  const close = () => setOpen(false);

  return (
    <>
      <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-900/5">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-3xl font-extrabold text-ink" data-testid="widget-price">
            {money(isOnce ? listing.flat_rate : isTiered ? (weekPrices[0] || 0) : weeklyLike ? listing.weekly_rate : listing.daily_rate)}
            <span className="text-base font-medium text-slate-500">{isOnce ? " one-time" : ` / ${isTiered || weeklyLike ? "week" : "day"}`}</span>
          </p>
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Available
          </span>
        </div>
        {isDual && (
          <p className="mt-2 text-xs font-medium text-slate-500" data-testid="dual-price-note">
            {withPackage
              ? `Package rate applied — or ${money(listing.daily_rate)} / day rented alone`
              : `Rent with any tote package for ${money(listing.weekly_rate)} / week`}
          </p>
        )}
        {isTiered && (
          <p className="mt-2 text-xs font-medium text-slate-500" data-testid="tiered-price-note">
            1–4 week terms — longer rentals unlock discounted weekly rates
          </p>
        )}
        <div className={isOnce ? "mt-6" : "mt-6 grid grid-cols-2 gap-3"}>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Delivery
            </span>
            <input
              type="date"
              min={today}
              value={start}
              onChange={(e) => pickStart(e.target.value)}
              data-testid="booking-start-date"
              className={inputCls}
            />
          </label>
          {isTiered && (
            <div>
              <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Pickup
              </span>
              <div className="flex h-12 w-full items-center rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-medium text-slate-600" data-testid="computed-pickup">
                {start ? `${fmtDate(end)} · ${days} days` : "Set by your term"}
              </div>
            </div>
          )}
          {!isOnce && !isTiered && (
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Pickup
              </span>
              <input
                type="date"
                min={start || today}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="booking-end-date"
                className={inputCls}
              />
            </label>
          )}
        </div>
        {isTiered && (
          <div className="mt-5" data-testid="duration-selector">
            <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Rental term — multi-week discounts
            </p>
            <div className="grid grid-cols-4 gap-2">
              {weekPrices.map((p, i) => {
                const n = i + 1;
                const active = durationWeeks === n;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`duration-option-${n}`}
                    onClick={() => {
                      setDurationWeeks(n);
                      if (start) {
                        const d = new Date(`${start}T00:00:00Z`);
                        d.setUTCDate(d.getUTCDate() + 7 * n - 1);
                        setEnd(d.toISOString().slice(0, 10));
                      }
                    }}
                    className={`rounded-xl border px-2 py-2.5 text-center transition-all duration-200 ${active ? "border-royal bg-royal text-white shadow-md shadow-royal/25" : "border-slate-200 bg-paper text-ink hover:border-royal/60"}`}
                  >
                    <span className="block text-xs font-bold">{n} wk</span>
                    <span className={`mt-0.5 block text-[11px] font-semibold ${active ? "text-white/80" : "text-slate-500"}`}>{money(p)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <AvailabilityCalendar booked={booked} start={start} onPick={pickStart} inventory={inventory} packageQty={packageQty} />
        {packageQty > 0 && days > 0 && totesLeft != null && (
          insufficient ? (
            <p data-testid="booking-inventory-warning" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-xs font-semibold text-red-600">
              Only {totesLeft} of {inventory} totes are available for these dates — this package needs {packageQty}. Try different dates or a smaller package.
            </p>
          ) : (
            <p data-testid="booking-inventory-status" className={`mt-4 rounded-xl px-4 py-3 text-xs font-semibold ${totesLeft < inventory && totesLeft < packageQty * 2 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
              {totesLeft} of {inventory} totes available for these dates{totesLeft < inventory && totesLeft < packageQty * 2 ? " — going fast" : ""}.
            </p>
          )
        )}
        {!packageQty && overlap && (
          <p data-testid="booking-overlap-warning" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-xs font-semibold text-red-600">
            These dates overlap an existing reservation — pick another window.
          </p>
        )}
        {isPackage && (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-paper p-4" data-testid="dolly-stepper">
            <div>
              <p className="text-xs font-bold text-ink">Rolling tote dolly — 1 included</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Extra dollies $25 / week each
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setExtraDollies(Math.max(0, extraDollies - 1))} data-testid="dolly-minus" aria-label="Remove extra dolly" className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 bg-white text-ink transition-colors duration-200 hover:border-royal hover:text-royal">
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-5 text-center text-sm font-bold text-ink" data-testid="dolly-count">{extraDollies}</span>
              <button type="button" onClick={() => setExtraDollies(Math.min(4, extraDollies + 1))} data-testid="dolly-plus" aria-label="Add extra dolly" className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 bg-white text-ink transition-colors duration-200 hover:border-royal hover:text-royal">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
        {isTiered && (
          <label className="mt-5 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-paper p-4 transition-colors duration-200 hover:border-royal/50" data-testid="dolly-bundle-toggle">
            <div>
              <p className="text-xs font-bold text-ink">Tote Dolly + Label Bundle</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Add-on · $25 / week · 40 labels & markers
              </p>
            </div>
            <input
              type="checkbox"
              checked={dollyBundle}
              onChange={(e) => setDollyBundle(e.target.checked)}
              className="h-5 w-5 accent-royal"
              data-testid="dolly-bundle-checkbox"
            />
          </label>
        )}
        {isTiered && (
          <label className="mt-5 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-paper p-4 transition-colors duration-200 hover:border-royal/50" data-testid="packing-kit-toggle">
            <div>
              <p className="text-xs font-bold text-ink">Packing Paper + Cushion Kit</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Add-on · $20 one-time · yours to keep
              </p>
            </div>
            <input
              type="checkbox"
              checked={packingKit}
              onChange={(e) => setPackingKit(e.target.checked)}
              className="h-5 w-5 accent-royal"
              data-testid="packing-kit-checkbox"
            />
          </label>
        )}
        {isDual && (
          <label className="mt-5 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-paper p-4 transition-colors duration-200 hover:border-royal/50" data-testid="with-package-toggle">
            <div>
              <p className="text-xs font-bold text-ink">Renting with a tote package</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Bills weekly · {money(listing.weekly_rate)} / week
              </p>
            </div>
            <input
              type="checkbox"
              checked={withPackage}
              onChange={(e) => {
                const on = e.target.checked;
                setWithPackage(on);
                if (on && start) {
                  const d = new Date(`${start}T00:00:00Z`);
                  d.setUTCDate(d.getUTCDate() + 6);
                  setEnd(d.toISOString().slice(0, 10));
                }
              }}
              className="h-5 w-5 accent-royal"
              data-testid="with-package-checkbox"
            />
          </label>
        )}
        {!isOnce && (
          <label className="mt-5 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-200 bg-paper p-4 transition-colors duration-200 hover:border-royal/50" data-testid="self-pickup-toggle">
            <div>
              <p className="text-xs font-bold text-ink">Self pick-up & return</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Bend warehouse · save $25
              </p>
            </div>
            <input type="checkbox" checked={selfPickup} onChange={(e) => setSelfPickup(e.target.checked)} className="h-5 w-5 accent-royal" data-testid="self-pickup-checkbox" />
          </label>
        )}
        <div className="mt-6 space-y-2.5 border-t border-slate-100 pt-5 text-sm">
          {isOnce ? (
            <div className="flex justify-between text-slate-600">
              <span>One-time add-on — yours to keep</span>
              <span className="font-semibold text-ink">{days ? money(rental) : "—"}</span>
            </div>
          ) : isTiered ? (
            <>
              <div className="flex justify-between text-slate-600">
                <span>{durationWeeks}-week package · {days || "—"} days</span>
                <span className="font-semibold text-ink">{days ? money(rental) : "—"}</span>
              </div>
              {tierSavings > 0 && (
                <div className="flex justify-between text-slate-600" data-testid="tier-savings-line">
                  <span>Multi-week savings (applied)</span>
                  <span className="font-semibold text-emerald-600">−{money(tierSavings)}</span>
                </div>
              )}
            </>
          ) : weeklyLike ? (
            <>
              <div className="flex justify-between text-slate-600">
                <span>First week · 7 days included</span>
                <span className="font-semibold text-ink">{days ? money(listing.weekly_rate) : "—"}</span>
              </div>
              {extraDays > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>+ {extraDays} extra day{extraDays === 1 ? "" : "s"} × {money(listing.extra_day_rate)}</span>
                  <span className="font-semibold text-ink">{money(extraDays * (listing.extra_day_rate || 0))}</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-between text-slate-600">
              <span>{money(listing.daily_rate)} × {days || "—"} day{days === 1 ? "" : "s"}</span>
              <span className="font-semibold text-ink">{days ? money(subtotal) : "—"}</span>
            </div>
          )}
          {isPackage && (
            <div className="flex justify-between text-slate-600">
              <span>Rolling tote dolly</span>
              <span className="font-semibold text-emerald-600">1 included</span>
            </div>
          )}
          {extraDollies > 0 && (
            <div className="flex justify-between text-slate-600" data-testid="dolly-fee-line">
              <span>+ {extraDollies} extra doll{extraDollies === 1 ? "y" : "ies"} · $25 × {weeks} wk</span>
              <span className="font-semibold text-ink">{money(dollyFee)}</span>
            </div>
          )}
          {bundleFee > 0 && (
            <div className="flex justify-between text-slate-600" data-testid="dolly-bundle-line">
              <span>+ Dolly bundle · $25 × {weeks} wk</span>
              <span className="font-semibold text-ink">{money(bundleFee)}</span>
            </div>
          )}
          {kitFee > 0 && (
            <div className="flex justify-between text-slate-600" data-testid="packing-kit-line">
              <span>+ Packing kit · one-time, due today</span>
              <span className="font-semibold text-ink">{money(kitFee)}</span>
            </div>
          )}
          {selfPickup && (
            <div className="flex justify-between text-slate-600" data-testid="self-pickup-line">
              <span>Self pick-up & return</span>
              <span className="font-semibold text-emerald-600">−{money(25)}</span>
            </div>
          )}
          {returningDisc > 0 && (
            <div className="flex justify-between text-slate-600" data-testid="returning-discount-line">
              <span className="font-semibold text-emerald-700">Welcome back — 10% returning-customer discount</span>
              <span className="font-semibold text-emerald-600">−{money(returningDisc)}</span>
            </div>
          )}
          <div className="flex justify-between text-slate-600">
            <span>Rental total</span>
            <span className="font-semibold text-ink">{days ? money(total) : "—"}</span>
          </div>
          {balanceDue > 0 && (
            <div className="flex justify-between text-slate-600">
              <span>Due on delivery (remaining 50%)</span>
              <span className="font-semibold text-ink">{days ? money2(balanceDue) : "—"}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-slate-100 pt-3">
            <span className="font-semibold text-ink">{isOnce ? "Due today" : "Due today — 50% deposit"}</span>
            <span data-testid="booking-total" className="font-display text-xl font-extrabold text-royal">
              {days ? money2(reserveNow) : "—"}
            </span>
          </div>
        </div>
        {pause?.paused && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-xs font-semibold leading-relaxed text-amber-800" data-testid="booking-paused-note">
            {pause.message}
          </div>
        )}
        <button
          onClick={reserve}
          disabled={blocked || pause?.paused}
          data-testid="reserve-now-button"
          className="mt-6 flex h-13 w-full items-center justify-center gap-2 rounded-xl bg-royal py-4 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.01] hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Reserve now <ArrowRight className="h-4 w-4" />
        </button>
        <p className="mt-3.5 flex items-center justify-center gap-1.5 text-xs text-slate-500">
          <BadgeCheck className="h-3.5 w-3.5 text-royal" /> Free cancellation up to 48h before delivery
        </p>
        <p className="mt-2 text-center text-[11px] leading-relaxed text-slate-400">
          Deposit applies to your rental total — the balance is collected on delivery. Damaged or
          missing equipment is billed per the rental agreement, up to full retail value ($25/box · $80/dolly).
        </p>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl"
              data-testid="checkout-modal"
            >
              <button
                onClick={close}
                aria-label="Close checkout"
                data-testid="checkout-close-button"
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
              >
                <X className="h-4 w-4" />
              </button>
              {step < 4 && (
                <>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                    Checkout — Step {step + 1} of 4
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-bold text-ink">
                    {["Review rental", "Contact details", "Rental agreement", "Payment"][step]}
                  </h3>
                </>
              )}

              {step === 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-4 rounded-2xl border border-slate-200 p-4">
                    <img src={listing.image} alt="" className="h-16 w-16 rounded-xl object-cover" />
                    <div>
                      <p className="font-display text-sm font-bold text-ink">{listing.title}</p>
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        {fmtDate(start)} – {fmtDate(end)} · {days} day{days === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 space-y-2 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>{isOnce ? "One-time add-on" : isTiered ? `${durationWeeks}-week package` : weeklyLike ? `Weekly package${extraDays ? ` + ${extraDays} extra day${extraDays === 1 ? "" : "s"}` : ""}` : "Rental subtotal"}</span><span className="font-semibold text-ink">{money(subtotal)}</span>
                    </div>
                    {extraDollies > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Extra dollies</span><span className="font-semibold text-ink">{money(dollyFee)}</span>
                      </div>
                    )}
                    {bundleFee > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Dolly bundle · $25 × {weeks} wk</span><span className="font-semibold text-ink">{money(bundleFee)}</span>
                      </div>
                    )}
                    {kitFee > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Packing kit (one-time, due today)</span><span className="font-semibold text-ink">{money(kitFee)}</span>
                      </div>
                    )}
                    {selfPickup && (
                      <div className="flex justify-between text-slate-600">
                        <span>Self pick-up discount</span><span className="font-semibold text-emerald-600">−{money(25)}</span>
                      </div>
                    )}
                    {balanceDue > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Due on delivery (remaining 50%)</span><span className="font-semibold text-ink">{money2(balanceDue)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-slate-100 pt-3">
                      <span className="font-semibold text-ink">Rental total</span>
                      <span className="font-display text-lg font-extrabold text-ink">{money(total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold text-royal">{isOnce ? "Due today" : "Due today — 50% deposit"}</span>
                      <span className="font-display text-lg font-extrabold text-royal">{money2(reserveNow)}</span>
                    </div>
                  </div>
                  {error && <p data-testid="checkout-error" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-xs font-semibold text-red-600">{error}</p>}
                  <button
                    onClick={() => setStep(1)}
                    data-testid="checkout-continue-button"
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark"
                  >
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              )}

              {step === 1 && (
                <div className="mt-6 space-y-5">
                  <div className="space-y-3">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Who's renting</p>
                    <input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Responsible party — full name *" data-testid="contact-name-input" className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="Email address *" type="email" data-testid="contact-email-input" className={inputCls} />
                      <input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="Cell phone *" data-testid="contact-phone-input" className={inputCls} />
                    </div>
                    <input value={customer.card_holder} onChange={(e) => setCustomer({ ...customer, card_holder: e.target.value })} placeholder="Name on card (card holder) *" data-testid="contact-card-holder-input" className={inputCls} />
                    <div>
                      <input value={customer.license_number} onChange={(e) => setCustomer({ ...customer, license_number: e.target.value })} placeholder="Driver's license number *" data-testid="contact-license-input" className={inputCls} />
                      <p className="mt-1 text-[10px] text-slate-400">Required for equipment rentals — kept private and never shared.</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Delivery & pickup</p>
                    <input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} placeholder="Street address to deliver to / pick up from *" data-testid="contact-address-input" className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={customer.zip} onChange={(e) => setCustomer({ ...customer, zip: e.target.value })} placeholder="Zip code *" inputMode="numeric" maxLength={5} data-testid="contact-zip-input" className={inputCls} />
                      <div className="grid grid-cols-2 gap-2" data-testid="contact-purpose">
                        {[["residential", "Residential"], ["commercial", "Commercial"]].map(([val, label]) => (
                          <button
                            key={val}
                            type="button"
                            onClick={() => setCustomer({ ...customer, purpose: val })}
                            data-testid={`contact-purpose-${val}`}
                            className={`rounded-xl border px-2 py-3 text-xs font-bold transition-all duration-200 ${customer.purpose === val ? "border-royal bg-royal text-white" : "border-slate-200 bg-paper text-ink hover:border-royal/60"}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="time-windows">
                      {[["Delivery window", deliveryWindow, setDeliveryWindow, "delivery-window"], ["Pickup window", pickupWindow, setPickupWindow, "pickup-window"]].map(([label, val, setter, tid]) => (
                        <div key={tid}>
                          <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{label} — 7 days a week</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {[["morning", "9am–12pm"], ["evening", "4pm–7pm"], ["call", "Please call to discuss"]].map(([v, l]) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setter(v)}
                                data-testid={`${tid}-${v}`}
                                className={`rounded-xl border px-2 py-2.5 text-[11px] font-bold leading-tight transition-all duration-200 ${val === v ? "border-royal bg-royal text-white" : "border-slate-200 bg-paper text-ink hover:border-royal/60"}`}
                              >
                                {l}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Emergency contact</p>
                    <input value={customer.alt_name} onChange={(e) => setCustomer({ ...customer, alt_name: e.target.value })} placeholder="Alternate contact name *" data-testid="contact-alt-name-input" className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input value={customer.alt_phone} onChange={(e) => setCustomer({ ...customer, alt_phone: e.target.value })} placeholder="Alt. phone *" data-testid="contact-alt-phone-input" className={inputCls} />
                      <input value={customer.alt_email} onChange={(e) => setCustomer({ ...customer, alt_email: e.target.value })} placeholder="Alt. email *" type="email" data-testid="contact-alt-email-input" className={inputCls} />
                    </div>
                  </div>
                  <textarea
                    value={customer.notes}
                    onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                    placeholder="Special notes for delivery — gate codes, house notes, drop spot… (optional)"
                    rows={2}
                    data-testid="contact-notes-input"
                    className={`${inputCls} resize-none`}
                  />
                  <div>
                    <input
                      value={customer.referred_by}
                      onChange={(e) => setCustomer({ ...customer, referred_by: e.target.value })}
                      placeholder="Who referred you? — their name or email (optional)"
                      data-testid="contact-referral-input"
                      className={inputCls}
                    />
                    <p className="mt-1 text-[10px] text-slate-400">Every referral that books this season enters them to win a dinner at Pastinni in Bend — a $100 value.</p>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <button onClick={() => setStep(0)} data-testid="checkout-back-button" className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-royal hover:text-royal">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button
                      onClick={() => {
                        if (!customer.name.trim() || !customer.email.includes("@") || customer.phone.trim().length < 7
                          || !/^\d{5}$/.test(customer.zip) || !customer.card_holder.trim() || customer.license_number.trim().length < 5 || !customer.address.trim()
                          || !customer.alt_name.trim() || customer.alt_phone.trim().length < 7 || !customer.alt_email.includes("@"))
                          return toast.error("Complete all required fields — contact, card holder, delivery address and emergency contact");
                        setStep(2);
                      }}
                      data-testid="checkout-contact-continue"
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark"
                    >
                      Continue <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="mt-6">
                  <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-200 bg-paper p-5" data-testid="agreement-document">
                    <RentalAgreement />
                  </div>
                  <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-colors duration-200 hover:border-royal/50" data-testid="agreement-toggle">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-5 w-5 shrink-0 accent-royal"
                      data-testid="agreement-checkbox"
                    />
                    <span className="text-xs font-medium leading-relaxed text-ink">
                      I have read and agree to the Oregon Tote Rentals Terms & Conditions and Rental
                      Agreement, including the damage, loss and cleaning fee schedule.
                    </span>
                  </label>
                  <div className="flex gap-3 pt-4">
                    <button onClick={() => setStep(1)} className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-royal hover:text-royal">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button
                      onClick={() => (agreed ? setStep(3) : toast.error("Review and accept the rental agreement to continue"))}
                      disabled={!agreed}
                      data-testid="agreement-continue-button"
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Continue to payment <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="mt-6 space-y-4">
                  <div className="rounded-2xl bg-royal-light p-4">
                    <p className="flex items-center gap-2 text-xs font-semibold text-royal">
                      <CreditCard className="h-4 w-4" /> Secure card checkout by Stripe. Test mode — use card 4242 4242 4242 4242.
                    </p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>{isTiered ? `${durationWeeks}-week package` : weeklyLike ? "Weekly package" : "Rental"} · {days} day{days === 1 ? "" : "s"}</span>
                      <span className="font-semibold text-ink">{money(subtotal)}</span>
                    </div>
                    {balanceDue > 0 && (
                      <div className="flex justify-between text-slate-600">
                        <span>Due on delivery (remaining 50%)</span>
                        <span className="font-semibold text-ink">{money2(balanceDue)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-slate-100 pt-3">
                      <span className="font-semibold text-ink">{isOnce ? "Charged today" : "Charged today — 50% deposit"}</span>
                      <span className="font-display text-lg font-extrabold text-royal">{money2(reserveNow)}</span>
                    </div>
                  </div>
                  {error && <p data-testid="checkout-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-semibold text-red-600">{error}</p>}
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setStep(2)} className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-royal hover:text-royal">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button
                      onClick={submit}
                      disabled={busy}
                      data-testid="checkout-submit-button"
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50"
                    >
                      {busy ? "Redirecting…" : `Pay ${money2(reserveNow)} deposit`}
                    </button>
                  </div>
                </div>
              )}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
